// One event store and at most one SSE connection per job, shared by every useJobStream(id)
// and consulted by useJob/useArtifacts to stop polling once the stream says the run is over.
//   - Opens only once the record is known and names a node (GET /events on a job that was never
//     dispatched is a 409, not a stream); re-checks whenever the record changes.
//   - `end` with a terminal state (stream or record): store.end() and close, or the browser
//     would reconnect and replay. `end` before that (a node that closed early) is retried.
//   - Errors close the EventSource (its own retry would replay from=0) and reconnect from
//     lastSeq + 1 after 2, 4, 8, 16, then every 30 s; a terminal record gives up after 5 tries.
//   - A finished job (terminal record) whose stream goes quiet for 20 s without its end is closed:
//     the backend can stall a replay part-way; what arrived stays, and reconnect() tries again.
//   - Released entries keep their connection for 3 s (StrictMode, quick remounts) and their
//     store for 5 min, so returning to a report resumes from lastSeq + 1 instead of replaying.
//     release(entry, { graceMs: 0 }) closes at once (a list peek that finished: its socket is needed elsewhere).
import { openEventStream, type EventStreamSource } from "../lib/api";
import { createEventStore, deriveEffective, isTerminal, type CreateStoreOptions, type EventStore } from "../lib/eventStore";
import type { Event, Job } from "../types";

/** The transport the registry drives. Was an EventSource; now a polling
 *  loop over GET /v1/jobs/{id}/events. The shape is unchanged. */
export type StreamSource = EventStreamSource;
type OpenStream = (id: string, from: number, onEvent: (e: Event) => void, onError: (e: unknown) => void) => StreamSource;

export type StreamEntry = {
  readonly id: string;
  readonly store: EventStore;
  /** Reconnect now (the "Retry now" action). */
  readonly reconnect: () => void;
};

type Internal = StreamEntry & {
  refs: number;
  record: Job | undefined;
  source: StreamSource | null;
  retryTimer: ReturnType<typeof setTimeout> | null;
  idleTimer: ReturnType<typeof setTimeout> | null;
  lastData: number; // receipt time of the latest event (or of the open)
  closeTimer: ReturnType<typeof setTimeout> | null;
  attempt: number;
  openedAt: number; // lastSeq when the current connection opened
  releasedAt: number | null;
};

type RegistryOptions = {
  open: OpenStream;
  now?: () => number;
  store?: CreateStoreOptions;
  backoffSec?: readonly number[];
  releaseGraceMs?: number;
  keepMs?: number;
  maxKept?: number;
  giveUpAfter?: number;
  idleMs?: number;
};

export function createStreamRegistry(opts: RegistryOptions) {
  const now = opts.now ?? Date.now;
  const backoff = opts.backoffSec ?? [2, 4, 8, 16, 30];
  const graceMs = opts.releaseGraceMs ?? 3000;
  const keepMs = opts.keepMs ?? 5 * 60_000;
  const maxKept = opts.maxKept ?? 12;
  const giveUpAfter = opts.giveUpAfter ?? 5;
  const idleMs = opts.idleMs ?? 20_000;
  const entries = new Map<string, Internal>();

  function verdictOnStream(e: Internal): "yes" | "wait" | "no" {
    if (e.refs === 0 || e.store.getSnapshot().ended) return "no";
    const r = e.record;
    if (!r) return "wait";
    // Never dispatched (queued, or cancelled before dispatch): the backend answers 409, not a stream.
    if (!r.node_id) return "no";
    return "yes";
  }

  function clearRetry(e: Internal) {
    if (e.retryTimer) clearTimeout(e.retryTimer);
    e.retryTimer = null;
  }

  function closeSource(e: Internal) {
    e.source?.close();
    e.source = null;
    if (e.idleTimer) clearTimeout(e.idleTimer);
    e.idleTimer = null;
  }

  /** While a finished job streams, close the connection once it has been quiet for idleMs. */
  function watchIdle(e: Internal) {
    if (e.idleTimer || !e.source || !isTerminal(e.record?.state)) return;
    const src = e.source;
    const check = () => {
      e.idleTimer = null;
      if (e.source !== src) return;
      const quiet = now() - e.lastData;
      if (quiet < idleMs) {
        e.idleTimer = setTimeout(check, idleMs - quiet);
        return;
      }
      closeSource(e);
      e.store.setConnection("closed");
    };
    e.idleTimer = setTimeout(check, idleMs);
  }

  function open(e: Internal) {
    clearRetry(e);
    closeSource(e);
    e.store.flushNow(); // apply anything still queued so the resume point is current
    const lastSeq = e.store.getSnapshot().lastSeq;
    e.openedAt = lastSeq;
    e.lastData = now();
    if (e.attempt === 0) e.store.setConnection("connecting");
    const src: StreamSource = opts.open(
      e.id,
      lastSeq > 0 ? lastSeq + 1 : 0,
      (ev) => {
        if (e.source !== src) return;
        if (typeof ev?.seq === "number" && ev.seq > e.openedAt) e.attempt = 0; // new data: the link works
        e.lastData = now();
        e.store.push(ev);
      },
      (err) => {
        // A malformed payload is skipped; anything else is the connection failing.
        if (err instanceof Error || e.source !== src) return;
        closeSource(e);
        retryLater(e);
      },
    );
    src.addEventListener("open", () => {
      if (e.source === src) e.store.setConnection("open");
    });
    src.addEventListener("end", () => {
      if (e.source !== src) return;
      closeSource(e);
      e.store.flushNow();
      const snap = e.store.getSnapshot();
      if (isTerminal(snap.state?.state) || isTerminal(e.record?.state)) e.store.end();
      else retryLater(e); // closed before the run finished: look again later
    });
    e.source = src;
    watchIdle(e);
  }

  function retryLater(e: Internal) {
    clearRetry(e);
    if (verdictOnStream(e) !== "yes") {
      e.store.setConnection("closed");
      return;
    }
    if (isTerminal(e.record?.state) && e.attempt >= giveUpAfter) {
      e.store.setConnection("closed");
      return;
    }
    const delay = backoff[Math.min(e.attempt, backoff.length - 1)] * 1000;
    e.attempt++;
    e.store.setConnection("reconnecting", { attempt: e.attempt, at: now() + delay });
    e.retryTimer = setTimeout(() => {
      e.retryTimer = null;
      if (verdictOnStream(e) === "yes") open(e);
      else e.store.setConnection("closed");
    }, delay);
  }

  function sync(e: Internal) {
    const v = verdictOnStream(e);
    if (v === "yes") {
      if (!e.source && !e.retryTimer) open(e);
      return;
    }
    if (v === "no" && e.refs > 0) {
      clearRetry(e);
      closeSource(e);
      if (!e.store.getSnapshot().ended) e.store.setConnection("closed");
    }
  }

  function sweep() {
    const t = now();
    const released = [...entries.values()].filter((x) => x.refs === 0 && x.releasedAt !== null);
    for (const x of released) if (t - (x.releasedAt ?? t) > keepMs) drop(x);
    const kept = released.filter((x) => entries.has(x.id)).sort((a, b) => (a.releasedAt ?? 0) - (b.releasedAt ?? 0));
    for (let i = 0; i < kept.length - maxKept; i++) drop(kept[i]);
  }

  function drop(e: Internal) {
    clearRetry(e);
    if (e.closeTimer) clearTimeout(e.closeTimer);
    closeSource(e);
    entries.delete(e.id);
  }

  function entryFor(id: string): StreamEntry {
    const existing = entries.get(id);
    if (existing) return existing;
    sweep();
    const e: Internal = {
      id,
      store: createEventStore(opts.store),
      refs: 0,
      record: undefined,
      source: null,
      retryTimer: null,
      idleTimer: null,
      lastData: 0,
      closeTimer: null,
      attempt: 0,
      openedAt: 0,
      releasedAt: now(),
      reconnect: () => {
        if (verdictOnStream(e) !== "yes") return;
        e.attempt = 0;
        open(e);
      },
    };
    entries.set(id, e);
    return e;
  }

  const internal = (entry: StreamEntry) => entry as Internal;

  return {
    entryFor,

    acquire(entry: StreamEntry) {
      const e = internal(entry);
      if (!entries.has(e.id)) entries.set(e.id, e); // swept between render and commit
      e.refs++;
      e.releasedAt = null;
      if (e.closeTimer) clearTimeout(e.closeTimer);
      e.closeTimer = null;
      sync(e);
    },

    release(entry: StreamEntry, opts: { graceMs?: number } = {}) {
      const e = internal(entry);
      e.refs = Math.max(0, e.refs - 1);
      if (e.refs > 0) return;
      e.releasedAt = now();
      if (e.closeTimer) clearTimeout(e.closeTimer);
      const close = () => {
        e.closeTimer = null;
        if (e.refs > 0) return;
        clearRetry(e);
        closeSource(e);
        e.attempt = 0;
        if (!e.store.getSnapshot().ended) e.store.setConnection("closed");
      };
      const grace = opts.graceMs ?? graceMs;
      if (grace <= 0) close();
      else e.closeTimer = setTimeout(close, grace);
    },

    /** Feed the latest job record (it gates opening and settles a stream that ends early). */
    setRecord(entry: StreamEntry, record: Job | undefined) {
      const e = internal(entry);
      const prev = e.record;
      e.record = record ?? prev;
      if (!record || (prev && prev.state === record.state && prev.node_id === record.node_id)) return;
      if (e.retryTimer && !e.source) {
        // the record moved (queued → dispatched, … → terminal): look now, not at the next backoff
        clearRetry(e);
        e.attempt = 0;
      }
      sync(e);
      watchIdle(e);
    },

    /** Effective liveness for polling decisions; undefined when no stream was ever opened here. */
    peekLive(id: string, record?: Job): boolean | undefined {
      const e = entries.get(id);
      if (!e) return undefined;
      const snap = e.store.getSnapshot();
      if (!snap.state && !snap.ended) return undefined;
      return deriveEffective(snap, record ?? e.record).live;
    },

    /** Test hook: live entries. */
    size: () => entries.size,
  };
}

type StreamRegistry = ReturnType<typeof createStreamRegistry>;

export const streams: StreamRegistry = createStreamRegistry({ open: openEventStream });
